/**
 * NewThreadContext
 *
 * This context provides a solution for sharing newly created thread IDs across components
 * before they are fully established in the URL or application state. It addresses a specific
 * challenge in our real-time messaging architecture:
 *
 * Problem:
 * When a new thread is created, we need to establish Pusher channel subscriptions immediately,
 * even before the thread creation process is complete and the URL is updated. Multiple
 * components need access to this new thread ID to properly handle events:
 *
 * 1. StreamHandler - Needs to subscribe to the thread channel for streaming events
 * 2. ToolEventsHandler - Needs to listen for tool and agent events on the thread channel
 * 3. Other components that may need to know about the new thread
 *
 * Without this context, we would need to pass the newThreadId as props through multiple
 * component layers (prop drilling), which becomes unwieldy as the application grows.
 *
 * Usage:
 * - Wrap your component tree with NewThreadProvider
 * - Use the useNewThread hook in any component that needs access to the new thread ID
 * - Call setNewThreadId when a new thread is created (typically in ThreadView)
 * - Access newThreadId in any component that needs it (StreamHandler, ToolEventsHandler, etc.)
 *
 * This context is particularly important for ensuring that real-time events are properly
 * handled during the thread creation process, when the URL hasn't yet been updated with
 * the new thread ID.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

interface NewThreadContextType {
  newThreadId: string | null;
  setNewThreadId: (id: string | null) => void;
}

const NewThreadContext = createContext<NewThreadContextType | undefined>(
  undefined,
);

interface NewThreadProviderProps {
  children: ReactNode;
}

export const NewThreadProvider: React.FC<NewThreadProviderProps> = ({
  children,
}) => {
  const [newThreadId, setNewThreadId] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  // Effect 1: Clear newThreadId when a new thread page is loaded (only depends on searchParams)
  useEffect(() => {
    console.log(
      'NewThreadContext: searchParams changed effect running',
      Date.now(),
    );

    // Get the current URL and serialize searchParams for comparison
    const currentUrl = window.location.href;
    const searchParamsString = JSON.stringify(
      Object.fromEntries(searchParams.entries()),
    );

    // Check if the URL contains a thread_id parameter
    const urlThreadId = searchParams.get('thread_id');

    // Check if we're on the new thread page
    const hasLoadedNewThreadPage =
      searchParams.get('new') === '' || searchParams.get('new') === 'true';

    console.log(
      'NewThreadContext: Current URL params:',
      searchParamsString,
      'urlThreadId:',
      urlThreadId,
      'hasLoadedNewThreadPage:',
      hasLoadedNewThreadPage,
      'currentUrl:',
      currentUrl,
    );

    // Clear newThreadId when we're on a new thread page WITHOUT a thread_id
    if (hasLoadedNewThreadPage && !urlThreadId && newThreadId) {
      console.log(
        `NewThreadContext: New thread page loaded without thread_id, clearing newThreadId`,
        {
          urlThreadId,
          hasLoadedNewThreadPage,
          newThreadId,
        },
      );
      setNewThreadId(null);
    }
  }, [searchParams]); // Only depends on searchParams

  // Effect 2: Clear newThreadId when a thread ID is detected (depends on both searchParams and newThreadId)
  useEffect(() => {
    // Check if the URL contains a thread_id parameter
    const urlThreadId = searchParams.get('thread_id');

    // Clear newThreadId when there's a thread_id and we have a newThreadId
    if (urlThreadId && newThreadId) {
      console.log(
        `NewThreadContext: Thread ID detected in URL, clearing newThreadId`,
        {
          urlThreadId,
          newThreadId,
        },
      );
      setNewThreadId(null);
    }
  }, [searchParams, newThreadId]);

  return (
    <NewThreadContext.Provider value={{ newThreadId, setNewThreadId }}>
      {children}
    </NewThreadContext.Provider>
  );
};

export const useNewThread = (): NewThreadContextType => {
  const context = useContext(NewThreadContext);
  if (context === undefined) {
    throw new Error('useNewThread must be used within a NewThreadProvider');
  }
  return context;
};
